import { useEffect } from "react";
import { gql, useSubscription } from "@apollo/client";
import CiroModal from "../../../shared/CiroModal";
import {
  AutomationListImportContactsModal_ImportContactsSubscription,
  AutomationListImportContactsModal_ImportContactsSubscriptionVariables,
} from "../../../../__generated__/graphql";

// Import steps
import UploadStep from "./ImportContactsModalUploadStep";
import MappingStep from "./ImportContactsModalMappingStep";
import ValidationStep from "./ImportContactsModalValidationStep";
import ProcessingStep from "./ImportContactsModalProcessingStep";
import CompletedStep from "./ImportContactsModalCompletedStep";
import { ImportStepEnum, useImportState } from "./useImportState";

// Define GraphQL subscription
export const AutomationListImportContactsModal_ImportContacts = gql`
  subscription AutomationListImportContactsModal_ImportContacts(
    $listId: Int!
    $contacts: [ContactListContactInput!]!
  ) {
    importContactsToList(listId: $listId, contacts: $contacts) {
      id
      processedCount
      totalCount
      duplicateContacts {
        firstName
        lastName
        title
        companyName
        companyWebsiteDomain
        companyLinkedinUrl
      }
      completedContacts {
        id
      }
      errors
    }
  }
`;

export interface IAutomationListImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactListId: number;
  onSuccess: () => void;
}

const AutomationListImportContactsModal: React.FC<
  IAutomationListImportContactsModalProps
> = ({ isOpen, onClose, contactListId, onSuccess }) => {
  const importState = useImportState();
  const {
    step,
    setStep,
    setIsProcessing,
    setImportProgress,
    setError,
    subscriptionEnabled,
    setSubscriptionEnabled,
    subscriptionVariables,
  } = importState;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Disable subscription when modal closes
      setSubscriptionEnabled(false);

      setTimeout(() => {
        importState.reset();
      }, 300);
    }
  }, [isOpen, importState, setSubscriptionEnabled]);

  // Handle subscription with Apollo useSubscription
  const { error: subscriptionError, restart: restartSubscription } =
    useSubscription<
      AutomationListImportContactsModal_ImportContactsSubscription,
      AutomationListImportContactsModal_ImportContactsSubscriptionVariables
    >(AutomationListImportContactsModal_ImportContacts, {
      variables: subscriptionVariables ?? { listId: 0, contacts: [] },
      skip: !subscriptionEnabled || !subscriptionVariables,
      onSubscriptionData: ({ subscriptionData }) => {
        if (subscriptionData.data?.importContactsToList) {
          const progress = subscriptionData.data.importContactsToList;

          setImportProgress({
            ...progress,
            duplicateContacts: progress.duplicateContacts || [],
            errors: progress.errors || [],
          });

          // When import is complete, move to the completed step
          if (progress.processedCount === progress.totalCount) {
            setSubscriptionEnabled(false);
            setIsProcessing(false);
            setStep(ImportStepEnum.COMPLETED);
          }
        }
      },
    });

  useEffect(() => {
    if (subscriptionError) {
      restartSubscription();
    }
  }, [subscriptionError, restartSubscription]);

  // Watch for subscription errors and update UI accordingly
  useEffect(() => {
    if (subscriptionError) {
      console.error("Subscription error:", subscriptionError);
      setError(subscriptionError.message || "An error occurred during import");
      setImportProgress({
        ...importState.importProgress,
        errors: [subscriptionError.message],
      });
      setStep(ImportStepEnum.VALIDATION);
      setIsProcessing(false);
      setSubscriptionEnabled(false);
    }
  }, [
    subscriptionError,
    setError,
    setImportProgress,
    setIsProcessing,
    setStep,
    setSubscriptionEnabled,
    importState,
  ]);

  return (
    <CiroModal size="XLARGE" isOpen={isOpen} onClose={onClose}>
      {step === ImportStepEnum.UPLOAD && (
        <UploadStep importState={importState} onClose={onClose} />
      )}
      {step === ImportStepEnum.MAPPING && (
        <MappingStep importState={importState} />
      )}
      {step === ImportStepEnum.VALIDATION && (
        <ValidationStep
          importState={importState}
          contactListId={contactListId}
        />
      )}
      {step === ImportStepEnum.PROCESSING && (
        <ProcessingStep importState={importState} />
      )}
      {step === ImportStepEnum.COMPLETED && (
        <CompletedStep
          importState={importState}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      )}
    </CiroModal>
  );
};

export default AutomationListImportContactsModal;
